import type {
  BookId, Catalog, ConnectionStatus, FamilyId, FamilySummary, LeagueId, LeagueSummary, OddsDisplay, TopicPhase, EndReason,
} from "@/lib/odds-screen/live/types";
import { boardMode, normalizeSearchText } from "@/lib/odds-screen/live/derive";
import { BOOK_REGULATIONS, type BookGroup, type BookRegulation } from "@/lib/book-catalog";

export const COLLEGE_LEAGUES = new Set(["cfb", "ncaab"]);

export const ODDS_DISPLAY_LABELS: Readonly<Record<OddsDisplay, string>> = {
  american: "American",
  decimal: "Decimal",
  percent: "Probability",
};

export const BOOK_GROUP_KEYS: Readonly<Partial<Record<BookRegulation, string>>> = {
  dfs: "D",
  federal: "P",
  sweepstakes: "S",
  offshore: "O",
};

export function bookGroupForKey(key: string): BookRegulation | undefined {
  return BOOK_REGULATIONS.find((regulation) => BOOK_GROUP_KEYS[regulation] === key.toUpperCase());
}

type BookGroupToggle = "missing" | { readonly ids: readonly BookId[]; readonly on: boolean };

export function toggleBookGroup(
  groups: readonly BookGroup[],
  isOn: (id: BookId) => boolean,
  saved: readonly BookId[],
  regulation: BookRegulation,
): BookGroupToggle {
  const members = groups.find((group) => group.regulation === regulation)?.books ?? [];
  if (members.length === 0) return "missing";
  if (members.some(isOn)) return { ids: members, on: false };
  const savedMembers = members.filter((id) => saved.includes(id));
  return { ids: savedMembers.length > 0 ? savedMembers : members, on: true };
}

export const LEAGUE_LABELS: Readonly<Record<string, string>> = {
  nfl: "NFL",
  cfb: "NCAAF",
  nba: "NBA",
  wnba: "WNBA",
  ncaab: "NCAAB",
  mlb: "MLB",
  nhl: "NHL",
  soccer: "Soccer",
  tennis: "Tennis",
  mma: "MMA",
  golf: "Golf",
  lol: "LoL",
  csgo: "CS2",
  cs2: "CS2",
  dota2: "Dota 2",
  valorant: "Valorant",
  cod: "CoD",
  r6: "R6",
  darts: "Darts",
};

export function leagueLabel(id: LeagueId): string {
  return LEAGUE_LABELS[id] ?? id.toUpperCase();
}

export type LeagueTab = { readonly id: LeagueId; readonly label: string; readonly events: number; readonly active: boolean };

export function leagueTabs(catalog: Catalog | null, active: LeagueId): readonly LeagueTab[] {
  if (catalog === null) return [];
  return catalog.leagueOrder.flatMap((id) => {
    const league = catalog.leagues.get(id);
    if (league === undefined || (id !== active && league.events === 0)) return [];
    return [{ id, label: leagueLabel(id), events: league.events, active: id === active }];
  });
}

const LEAGUE_SPORTS: Readonly<Record<string, string>> = {
  nfl: "Football",
  cfb: "Football",
  nba: "Basketball",
  wnba: "Basketball",
  ncaab: "Basketball",
  mlb: "Baseball",
  nhl: "Hockey",
  lol: "Esports",
  csgo: "Esports",
  cs2: "Esports",
  dota2: "Esports",
  valorant: "Esports",
  cod: "Esports",
  r6: "Esports",
};

export type SportGroup = { readonly key: string; readonly label: string; readonly leagues: readonly LeagueTab[]; readonly active: boolean };

export function sportGroups(tabs: readonly LeagueTab[]): readonly SportGroup[] {
  return bucketed(tabs, (tab) => LEAGUE_SPORTS[tab.id] ?? tab.id).map(([key, leagues]) => ({
    key,
    label: LEAGUE_SPORTS[leagues[0].id] ?? leagueLabel(key),
    leagues,
    active: leagues.some((league) => league.active),
  }));
}

export function firstLeagueWithGames(catalog: Catalog): LeagueId | null {
  for (const id of catalog.leagueOrder) {
    const league = catalog.leagues.get(id);
    if (league !== undefined && league.events > 0) return id;
  }
  return catalog.leagueOrder[0] ?? null;
}

export function marketDisplayOrder(specs: readonly { readonly line: "none" | "spread" | "total" }[]): readonly number[] {
  const rank = (line: "none" | "spread" | "total") => (line === "none" ? 0 : line === "spread" ? 1 : 2);
  return specs
    .map((spec, index) => ({ index, r: rank(spec.line) }))
    .sort((a, b) => a.r - b.r || a.index - b.index)
    .map((entry) => entry.index);
}

export function propsFamilies(league: LeagueSummary | undefined): readonly FamilySummary[] {
  return league === undefined ? [] : league.families.filter((entry) => entry.kind === "props");
}

function bucketed<T>(items: readonly T[], keyOf: (item: T) => string): readonly (readonly [string, readonly T[]])[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const bucket = buckets.get(key);
    if (bucket === undefined) buckets.set(key, [item]);
    else bucket.push(item);
  }
  return [...buckets];
}

export type LinesGroup = {
  readonly label: string;
  readonly category: string;
  readonly base: FamilySummary;
  readonly periods: readonly FamilySummary[];
};

function fullPeriodOnly(family: FamilySummary): boolean {
  return family.markets.every((market) => market.period === "full");
}

export function linesGroups(league: LeagueSummary | undefined): readonly LinesGroup[] {
  const lines = (league?.families ?? []).filter((family) => family.kind === "lines");
  return bucketed(lines, (family) => family.group ?? "").flatMap(([key, families]) => {
    const base = families.find(fullPeriodOnly) ?? families[0];
    const others = families.filter((entry) => entry !== base);
    if (key !== "") return [{ label: key, category: base.category, base, periods: others }];
    const standalone = others.filter(fullPeriodOnly).map((entry): LinesGroup => ({ label: entry.label, category: entry.category, base: entry, periods: [] }));
    return [{ label: base.label, category: base.category, base, periods: others.filter((entry) => !fullPeriodOnly(entry)) }, ...standalone];
  });
}

function inLinesGroup(group: LinesGroup, family: FamilyId | null): boolean {
  return family !== null && (family === group.base.id || group.periods.some((entry) => entry.id === family));
}

type LinesItem = { readonly family: FamilySummary; readonly label: string };

function shownLines(group: LinesGroup, primary: boolean, active: FamilyId | null): readonly LinesItem[] {
  const shown = [group.base, ...group.periods].filter((entry) => entry.events > 0 || entry.id === active || (primary && entry === group.base));
  const fullGame = fullPeriodOnly(group.base) && shown.some((entry) => !fullPeriodOnly(entry));
  return shown.map((family) => ({ family, label: fullGame && family === group.base ? "Full game" : family.label }));
}

export function gameLinesFamily(league: LeagueSummary | undefined): FamilySummary | undefined {
  return linesGroups(league).find((group) => boardMode(group.base) === "lines" && fullPeriodOnly(group.base))?.base;
}

export type PeriodChip = { readonly id: FamilyId; readonly label: string; readonly active: boolean };

export function periodChips(league: LeagueSummary | undefined, family: FamilyId | null): readonly PeriodChip[] {
  const groups = linesGroups(league);
  const index = groups.findIndex((group) => inLinesGroup(group, family));
  const group = groups[index];
  if (group === undefined) return [];
  const chips = shownLines(group, index === 0, family).map((item) => ({ id: item.family.id, label: item.label, active: item.family.id === family }));
  return chips.length < 2 ? [] : chips;
}

export type FamilyGroup = { readonly label: string; readonly families: readonly FamilySummary[] };
export type FamilyCategory = { readonly label: string; readonly groups: readonly FamilyGroup[] };

function sectionLabel(family: FamilySummary): string {
  return family.kind === "props" ? family.category : family.group ?? family.label;
}

export function gameSectionCategories(league: LeagueSummary | undefined): readonly FamilyCategory[] {
  const families = (league?.families ?? []).filter((family) => boardMode(family) !== "lines");
  const ordered = [...families.filter((family) => family.kind === "props"), ...families.filter((family) => family.kind === "lines")];
  return bucketed(ordered, (family) => family.category).map(([label, members]) => ({
    label,
    groups: bucketed(members, sectionLabel).map(([groupLabel, grouped]) => ({ label: groupLabel, families: grouped })),
  }));
}

export function familyTitle(family: FamilySummary): string {
  const group = family.group;
  return group !== undefined && !normalizeSearchText(family.label).includes(normalizeSearchText(group)) ? `${group} · ${family.label}` : family.label;
}

export type PickerPeriod = {
  readonly id: FamilyId;
  readonly label: string;
  readonly active: boolean;
};

export type PickerItem = {
  readonly id: FamilyId;
  readonly label: string;
  readonly active: boolean;
  readonly periods: readonly PickerPeriod[];
};

export type PickerCategory = {
  readonly key: string;
  readonly label: string;
  readonly section: "Game" | "Player props";
  readonly items: readonly PickerItem[];
  readonly opens: FamilyId | null;
  readonly active: boolean;
};

export function pickerCategories(league: LeagueSummary | undefined, family: FamilyId | null): readonly PickerCategory[] {
  if (league === undefined) return [];
  const markets = linesGroups(league).flatMap((group, index) => {
    const shown = shownLines(group, index === 0, family);
    if (shown.length === 0) return [];
    const periods = shown.map((entry): PickerPeriod => ({ id: entry.family.id, label: entry.label, active: entry.family.id === family }));
    const item: PickerItem = {
      id: periods[0].id,
      label: group.label,
      active: periods.some((period) => period.active),
      periods,
    };
    return [{ category: group.category, item }];
  });
  const gameCategories = bucketed(markets, (market) => market.category).map(([label, members]): PickerCategory => {
    const items = members.map((market) => market.item);
    const only = items.length === 1 && normalizeSearchText(items[0].label) === normalizeSearchText(label) ? items[0] : undefined;
    return { key: `lines:${label}`, label, section: "Game", items, opens: only?.id ?? null, active: items.some((item) => item.active) };
  });
  const propsCategories = bucketed(propsFamilies(league), (entry) => entry.category).flatMap(([label, families]): PickerCategory[] => {
    const items = families
      .filter((entry) => entry.rows > 0 || entry.id === family)
      .map((entry): PickerItem => ({ id: entry.id, label: entry.label, active: family === entry.id, periods: [] }));
    return items.length === 0 ? [] : [{ key: `props:${label}`, label, section: "Player props", items, opens: null, active: items.some((item) => item.active) }];
  });
  return [...gameCategories, ...propsCategories];
}

export function marketCount(count: number, unit: "games" | "players"): string {
  if (unit === "games") return `${count} ${count === 1 ? "game" : "games"}`;
  return `${count} ${count === 1 ? "player" : "players"}`;
}

export function activeMarketLabel(league: LeagueSummary | undefined, family: FamilyId | null): string {
  const fam = findFamily(league, family);
  if (fam === undefined) return "";
  return linesGroups(league).find((group) => inLinesGroup(group, fam.id))?.label ?? fam.label;
}

export function findFamily(league: LeagueSummary | undefined, family: FamilyId | null): FamilySummary | undefined {
  if (league === undefined) return undefined;
  if (family === null) return league.families[0];
  return league.families.find((entry) => entry.id === family);
}

export const FIRST_FAMILY: FamilyId = "game_lines";

export function resolveFamilyId(league: LeagueSummary | undefined, requested: string | null): FamilyId | null {
  if (league === undefined) return requested ?? FIRST_FAMILY;
  const matched = requested === null ? undefined : league.families.find((entry) => entry.id === requested);
  if (matched !== undefined) return matched.id;
  return league.families[0]?.id ?? null;
}

export type LivePill =
  | { readonly kind: "live" }
  | { readonly kind: "connecting" }
  | { readonly kind: "reconnecting" }
  | { readonly kind: "paused" }
  | { readonly kind: "limited" }
  | { readonly kind: "rate_limited" }
  | { readonly kind: "locked" };

export type BlockingPill = Extract<LivePill, { kind: "limited" | "locked" }>;

export function blockingPill(pill: LivePill): BlockingPill | null {
  return pill.kind === "limited" || pill.kind === "locked" ? pill : null;
}

export function livePill(status: ConnectionStatus): LivePill {
  switch (status) {
    case "live": return { kind: "live" };
    case "reconnecting": return { kind: "reconnecting" };
    case "paused": return { kind: "paused" };
    case "limited": return { kind: "limited" };
    case "rate_limited": return { kind: "rate_limited" };
    case "locked": return { kind: "locked" };
    case "connecting":
    case "idle":
    default:
      return { kind: "connecting" };
  }
}

export type BoardState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready" }
  | { readonly kind: "resyncing" }
  | { readonly kind: "ended"; readonly reason: EndReason }
  | { readonly kind: "empty_league" }
  | { readonly kind: "empty_family" }
  | { readonly kind: "empty_board" };

export function boardState(input: {
  readonly catalogReady: boolean;
  readonly leagueKnown: boolean;
  readonly family: FamilySummary | undefined;
  readonly phase: TopicPhase;
  readonly ended: EndReason | null;
  readonly rowCount: number;
  readonly hasValue: boolean;
}): BoardState {
  if (!input.catalogReady) return { kind: "loading" };
  if (!input.leagueKnown) return { kind: "empty_league" };
  if (input.family === undefined) return { kind: "empty_family" };
  if (input.ended !== null) return { kind: "ended", reason: input.ended };
  if (!input.hasValue) return { kind: "loading" };
  if (input.rowCount === 0) return { kind: "empty_board" };
  if (input.phase === "resyncing") return { kind: "resyncing" };
  return { kind: "ready" };
}
